import { API_BASE } from './apiBase';

/**
 * Version, die beim Build fest ins Bundle gebacken wurde (deploy/deploy.sh setzt
 * VITE_APP_VERSION). Lokal/ohne Deploy steht hier 'dev' — dann ist der
 * Update-Hinweis komplett aus, sonst würde er bei jedem `npm run dev` aufpoppen.
 */
export const APP_VERSION: string =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || 'dev';

export interface VersionHistoryEntry {
  version: string;
  build: number;
  releasedAt: string;
  commit?: string;
  changes: string[];
}

export interface VersionInfo {
  version: string;
  build: number;
  releasedAt: string | null;
  history: VersionHistoryEntry[];
}

export async function fetchVersionInfo(): Promise<VersionInfo> {
  // no-store: der Hinweis lebt davon, die WIRKLICH ausgelieferte Version zu sehen —
  // eine gecachte Antwort würde den Update-Hinweis beliebig lange verschlucken.
  const res = await fetch(`${API_BASE}/api/version`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Version nicht abrufbar');
  return res.json();
}

/**
 * Ist die laufende Oberfläche veraltet?
 * Nur wenn beide Seiten eine echte Version melden und sie sich unterscheiden.
 * 'dev' oder leere Werte → false (kein Hinweis in der Entwicklung).
 */
export function isOutdated(running: string, latest: string | null | undefined): boolean {
  if (!latest || latest === 'dev' || running === 'dev' || !running) return false;
  return running !== latest;
}

/** Mindestabstand zwischen zwei Update-Hinweisen. */
export const HINWEIS_ABSTAND_MS = 12 * 60 * 60 * 1000;

/** localStorage-Schlüssel: Zeitpunkt, zu dem zuletzt ein Update-Hinweis erschienen ist. */
export const HINWEIS_KEY = 'update_hinweis_zuletzt';

/**
 * Darf jetzt wieder ein Update-Hinweis erscheinen?
 *
 * Bei mehreren Deploys am Tag würde sonst nach jedem einzelnen ein Hinweis aufpoppen.
 * Stattdessen erscheint er höchstens alle 12 Stunden und fasst damit alles seit dem
 * letzten Hinweis zusammen — der Versionsverlauf im Modal zeigt ohnehin jede Änderung.
 *
 * Unbekannter/kaputter Zeitstempel → zeigen (lieber einmal zu viel als den Hinweis
 * dauerhaft verschlucken).
 */
export function darfHinweisZeigen(jetzt: number, zuletzt: string | number | null, abstandMs = HINWEIS_ABSTAND_MS): boolean {
  const ts = typeof zuletzt === 'string' ? Number(zuletzt) : zuletzt;
  if (ts == null || !Number.isFinite(ts) || ts <= 0) return true;
  // Uhr zurückgestellt oder Zeitstempel aus der Zukunft → nicht ewig blockieren.
  if (ts > jetzt) return true;
  return jetzt - ts >= abstandMs;
}
